import { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';

import { WaypointType } from '@/types/waypoint';
import { WAYPOINT_TYPE_LABELS, WAYPOINT_TYPE_COLORS, WAYPOINT_TYPE_DISPLAY_ORDER } from '@/components/map/map-utils';
import { iconSvgPath } from '@/components/map/WaypointMarker';
import { useBodyScrollLock } from '@/hooks/use-body-scroll-lock';

// ember-400 hex — used for selected tile icon stroke
const EMBER_400 = '#e5841f';

interface TypePickerSheetProps {
  isOpen: boolean;
  onTypeSelected: (type: WaypointType) => void;
  onClose: () => void;
}

export function TypePickerSheet({
  isOpen,
  onTypeSelected,
  onClose,
}: TypePickerSheetProps) {
  const [selectedType, setSelectedType] = useState<WaypointType>(WaypointType.Blind);
  // hasMounted gates the open translate class so the browser paints the
  // initial translate-y-full frame before the transition runs.
  const [hasMounted, setHasMounted] = useState(false);

  const dialogRef = useRef<HTMLDivElement>(null);
  // SAFETY: triggerRef stores whatever element was active before the sheet opened;
  // it is read only during the close transition, at which point the element may
  // or may not still be connected — we guard with isConnected before calling focus().
  const triggerRef = useRef<HTMLElement | null>(null);

  // Lock body scroll while sheet is open (fixes iOS Safari viewport divergence)
  useBodyScrollLock(isOpen);

  // Mount animation: set hasMounted after first paint so translate-y-full renders
  // before we apply translate-y-0.
  useEffect(() => {
    let rafId: number;
    rafId = requestAnimationFrame(() => {
      rafId = requestAnimationFrame(() => {
        setHasMounted(true);
      });
    });
    return () => cancelAnimationFrame(rafId);
  }, []);

  // Focus management: move focus into dialog when open, restore on close.
  useEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement as HTMLElement | null;
      dialogRef.current?.focus();
    } else {
      if (triggerRef.current?.isConnected) {
        triggerRef.current.focus();
      } else {
        document.body.focus();
      }
      triggerRef.current = null;
    }
  }, [isOpen]);

  // Escape key: only attach listener while sheet is open.
  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  function handleTypeSelect(type: WaypointType) {
    setSelectedType(type);
    onTypeSelected(type);
  }

  const sheetContent = (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-display-sm text-[var(--color-text-primary)]">What are you marking?</h2>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-md text-[var(--color-text-placeholder)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-elevated)] transition-colors"
          aria-label="Close"
        >
          <X size={20} />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {WAYPOINT_TYPE_DISPLAY_ORDER.map((type) => {
          const color = WAYPOINT_TYPE_COLORS[type];
          const label = WAYPOINT_TYPE_LABELS[type];
          const isSelected = selectedType === type;
          const strokeColor = isSelected ? EMBER_400 : color;

          return (
            <button
              key={type}
              type="button"
              onClick={() => handleTypeSelect(type)}
              className={`flex flex-col items-center gap-2 p-3 rounded-lg border transition-colors ${
                isSelected
                  ? 'border-[var(--color-accent)] bg-[var(--color-accent)]/10 text-[var(--color-accent)]'
                  : 'border-[var(--color-border)] bg-[var(--color-elevated)] text-[var(--color-text-muted)] hover:border-[var(--color-border-subtle)] hover:bg-[var(--color-elevated)]'
              }`}
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: color + '26' }} // 15% opacity via hex
                // SAFETY: iconSvgPath output is built from compile-time SVG asset strings and constant colors
                dangerouslySetInnerHTML={{ __html: iconSvgPath(type, 16, strokeColor) }}
              />
              <span className="text-body-xs font-medium text-center leading-tight">{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div data-state={isOpen ? 'open' : 'closed'}>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sheet */}
      <div
        ref={dialogRef}
        tabIndex={-1}
        className={`fixed inset-x-0 bottom-0 z-50 bg-[var(--color-surface)] border-t border-[var(--color-border)] rounded-t-2xl max-h-[85vh] overflow-y-auto transition-transform duration-300 focus:outline-none ${
          isOpen && hasMounted ? 'translate-y-0' : 'translate-y-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Add waypoint"
      >
        <div className="p-4 pb-8">{sheetContent}</div>
      </div>
    </div>
  );
}
